import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Check, X, Users } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ParentInvitation {
  id: string;
  student_email: string;
  student_name: string;
  grade: string | null;
  status: string;
  invited_at: string;
  invitation_code: string;
  parents?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

export default function ParentInvitations() {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<ParentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) {
      loadInvitations();
    }
  }, [user]);

  const loadInvitations = async () => {
    if (!user?.email) return;

    try {
      console.log('Loading invitations for email:', user.email);
      
      const { data, error } = await supabase
        .from('parent_invitations')
        .select('*')
        .eq('student_email', user.email)
        .eq('status', 'pending')
        .order('invited_at', { ascending: false });

      console.log('Invitations query result:', { data, error });

      if (error) throw error;

      // Do not filter by parent data; students may not have access to parent profiles due to RLS
      setInvitations(data || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId: string) => {
    if (!user?.id) return;

    setProcessing(invitationId);
    try {
      const { data, error } = await supabase.rpc('accept_parent_invitation', {
        _invitation_id: invitationId,
        _student_user_id: user.id
      });

      if (error) throw error;

      if (data) {
        toast.success('Parent invitation accepted!');
        setInvitations(invitations.filter(inv => inv.id !== invitationId));
      } else {
        toast.error('Failed to accept invitation');
      }
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (invitationId: string) => {
    setProcessing(invitationId);
    try {
      const { error } = await supabase
        .from('parent_invitations')
        .update({ status: 'rejected' })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success('Invitation rejected');
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast.error('Failed to reject invitation');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            Loading invitations...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          <CardTitle>Parent Invitations</CardTitle>
        </div>
        <CardDescription>
          You have {invitations.length} pending parent {invitations.length === 1 ? 'invitation' : 'invitations'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {invitations.map((invitation) => (
            <div 
              key={invitation.id}
              className="p-4 border rounded-lg bg-card space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">
                      {invitation.parents
                        ? `${invitation.parents.first_name} ${invitation.parents.last_name}`.trim()
                        : 'Parent/Guardian'}
                    </h3>
                    {invitation.parents?.email ? (
                      <p className="text-sm text-muted-foreground">{invitation.parents.email}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">A parent invited you via the Parent Portal</p>
                    )}
                  </div>
                </div>
                <Badge variant="secondary">Pending</Badge>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  wants to connect with you on the Parent Portal to view your:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Tutoring schedule</li>
                  <li>Test assignments and results</li>
                  <li>Academic progress</li>
                </ul>
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Invited {format(new Date(invitation.invited_at), 'MMM d, yyyy')}
                </span>
                {invitation.grade && (
                  <Badge variant="outline">Grade {invitation.grade}</Badge>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleAccept(invitation.id)}
                  disabled={processing === invitation.id}
                  className="flex-1"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Accept
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReject(invitation.id)}
                  disabled={processing === invitation.id}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
